/**
 * 拷贝数据
 * 支持深拷贝
 * @author luoying
 * @since 17/06/07
 */
import { getTag } from './check-types';
const stringTag = '[object String]';
const numberTag = '[object Number]';
const boolTag = '[object Boolean]';
const dateTag = '[object Date]';
const regExpTag = '[object RegExp]';
const mapTag = '[object Map]';
const setTag = '[object Set]';
const blobTag = '[object Blob]';
function cloneRegExp(regexp) {
    const result = new RegExp(regexp.source, regexp.flags);
    result.lastIndex = regexp.lastIndex;
    return result;
}
function cloneBlob(blob) {
    return new Blob([blob], { type: blob.type });
}
function cloneMap(input, isDeep, circularMap) {
    const map = new Map();
    // add circular reference
    circularMap.set(input, map);
    input.forEach((val, key) => {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        map.set(key, isDeep ? baseClone(val, true, circularMap) : val);
    });
    return map;
}
function cloneSet(input, isDeep, circularMap) {
    const set = new Set();
    // add circular reference
    circularMap.set(input, set);
    input.forEach(val => {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        set.add(isDeep ? baseClone(val, true, circularMap) : val);
    });
    return set;
}
function cloneArr(arr, isDeep, circularMap) {
    if (!isDeep)
        return arr.concat();
    // keep the length of the source array!
    const ret = new Array(arr.length);
    // add circular reference
    circularMap.set(arr, ret);
    arr.forEach((d, i) => {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        ret[i] = baseClone(d, true, circularMap);
    });
    return ret;
}
function cloneObj(obj, isDeep, circularMap) {
    const prototype = Object.getPrototypeOf(obj);
    const ret = Object.create(prototype);
    if (!isDeep)
        return Object.assign(ret, obj);
    // add circular reference
    circularMap.set(obj, ret);
    const keys = Object.keys(obj);
    keys.forEach(k => {
        const d = obj[k];
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        ret[k] = baseClone(d, true, circularMap);
    });
    return ret;
}
function baseClone(data, isDeep, circularMap) {
    if (data == null) {
        return data;
    }
    // function cannot be cloned
    // primitive value does not need to be cloned
    const primitive = [
        'string',
        'number',
        'bigint',
        'boolean',
        'symbol',
        'undefined',
        'function',
    ];
    if (primitive.includes(typeof data)) {
        return data;
    }
    // tag as `[object *]`
    const tag = getTag(data);
    if (tag === stringTag || tag === numberTag) {
        return new data.constructor(data);
    }
    if (tag === boolTag || tag === dateTag) {
        return new data.constructor(+data);
    }
    if (tag === regExpTag) {
        return cloneRegExp(data);
    }
    if (tag === blobTag) {
        return cloneBlob(data);
    }
    // such as: `Object`, `Array`, `Map`, `Set`
    // check for circular references and return its when exist
    const circular = circularMap.get(data);
    if (circular)
        return circular;
    if (tag === mapTag) {
        return cloneMap(data, isDeep, circularMap);
    }
    if (tag === setTag) {
        return cloneSet(data, isDeep, circularMap);
    }
    if (Array.isArray(data)) {
        return cloneArr(data, isDeep, circularMap);
    }
    return cloneObj(data, isDeep, circularMap);
}
/**
 * 拷贝数据
 * - 支持深拷贝
 * - 支持拷贝`Date`,`RegExp`,`Map`,`Set`,`Blob`,基本类型包装对象等数据
 * - 支持循环引用的深拷贝
 * @param  {T}  data   要拷贝的源数据
 * @param  {boolean} [isDeep=false] 是否深拷贝，默认浅拷贝
 * @return {T}         返回拷贝后的数据
 */
export default function clone(data, isDeep = false) {
    return baseClone(data, isDeep, new Map());
}
//# sourceMappingURL=clone.js.map